import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useLazyQuery } from "@apollo/client";
import {
  DialogContent,
  TextField,
  List,
  ListItemButton,
  ListItemText,
  Box,
  Alert,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { DaDataSuggestAddressQuery } from "@/shared/graphql";
import { DialogHeader, ResponsiveDialog } from "@/shared/components";
import type {
  DaDataAddressSuggestionType,
  DaDataLocationInputType,
} from "@/shared/types/common";
import { useDebounce } from "@/shared/hooks";

interface AddressSearchDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (address: DaDataAddressSuggestionType) => void;
  title?: string;
  regionId?: string | null;
}

export const AddressSearchDialog: React.FC<AddressSearchDialogProps> = ({
  open,
  onClose,
  onSelect,
  title = "Поиск адреса",
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  const [suggestAddress, { data, loading, error }] = useLazyQuery(
    DaDataSuggestAddressQuery,
    {
      fetchPolicy: "cache-and-network",
    }
  );

  // Автоматический поиск при изменении debounced query
  useEffect(() => {
    if (debouncedSearchQuery.trim().length >= 3) {
      const locations: DaDataLocationInputType[] = [];

      suggestAddress({
        variables: {
          query: debouncedSearchQuery,
          count: 10,
          locations: locations.length > 0 ? locations : undefined,
        },
      });
    }
  }, [debouncedSearchQuery, suggestAddress]);

  const suggestions = useMemo(() => {
    return data?.dadataSuggestAddress?.suggestions || [];
  }, [data]);

  const handleSelectAddress = useCallback(
    (suggestion: DaDataAddressSuggestionType) => {
      onSelect(suggestion);
      onClose();
      setSearchQuery("");
    },
    [onSelect, onClose]
  );

  const handleClose = useCallback(() => {
    onClose();
    setSearchQuery("");
  }, [onClose]);

  return (
    <ResponsiveDialog open={open} onClose={handleClose}>
      <DialogHeader title={title} onClose={handleClose} loading={loading} />

      <DialogContent
        sx={{
          p: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{ p: 2, borderBottom: 1, borderColor: "divider", flexShrink: 0 }}
        >
          <TextField
            fullWidth
            placeholder="Введите адрес, индекс или название улицы..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
                ),
              },
            }}
            variant="outlined"
            size="medium"
            helperText="Поиск по адресу, индексу или названию улицы"
          />
        </Box>

        <Box sx={{ flex: 1, overflow: "auto" }}>
          {error && (
            <Box sx={{ p: 2 }}>
              <Alert severity="error">
                Ошибка при поиске адресов: {error.message}
              </Alert>
            </Box>
          )}

          {!loading &&
            !error &&
            searchQuery.trim().length > 0 &&
            searchQuery.trim().length < 3 && (
              <Box sx={{ p: 2 }}>
                <Alert severity="info">
                  Введите минимум 3 символа для поиска
                </Alert>
              </Box>
            )}

          {!loading &&
            !error &&
            debouncedSearchQuery.trim().length >= 3 &&
            suggestions.length === 0 && (
              <Box sx={{ p: 2 }}>
                <Alert severity="warning">
                  По вашему запросу ничего не найдено
                </Alert>
              </Box>
            )}

          {suggestions.length > 0 && (
            <List>
              {suggestions.map(
                (suggestion: DaDataAddressSuggestionType, index: number) => (
                  <ListItemButton
                    key={index}
                    onClick={() => handleSelectAddress(suggestion)}
                    sx={{
                      borderBottom: 1,
                      borderColor: "divider",
                      "&:last-child": {
                        borderBottom: 0,
                      },
                      py: 2,
                    }}
                  >
                    <ListItemText
                      primary={suggestion.value}
                      secondary={suggestion.unrestricted_value}
                    />
                  </ListItemButton>
                )
              )}
            </List>
          )}

          {!loading && !error && searchQuery.trim().length === 0 && (
            <Box sx={{ p: 3, textAlign: "center", color: "text.secondary" }}>
              <SearchIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
              <Typography variant="h6" gutterBottom>
                Поиск адреса
              </Typography>
              <Typography variant="body2">
                Начните вводить адрес, индекс или название улицы
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
    </ResponsiveDialog>
  );
};
